import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Chip, Avatar,
  CircularProgress, InputAdornment, Divider, useMediaQuery, Link, Alert,
  IconButton, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Place as PlaceIcon,
  WorkOutline as WorkIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import PortalBrandingHeader from '../portal/PortalBrandingHeader';
import PortalHeader from '../portal/PortalHeader';
import { getBoardUrl, getPositionUrl } from './hostSlug';
import { clearFairAnswers } from '../portal/fairMemory';

const SESSION_TOKEN_KEY = 'candidate_session_token';

// La modalidad se guarda como código (wizard del ATS): se muestra traducida
const MODALIDADES = ['presencial', 'remoto', 'hibrido'];

// Fechas guardadas como 'YYYY-MM-DD' (fecha de cierre): se leen como fecha
// local; new Date('2026-11-30') es medianoche UTC y en Chile mostraba el día 29
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Copia un texto al portapapeles. Respaldo para navegadores sin la API del
// portapapeles: un textarea temporal con execCommand('copy').
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Sin permiso o sin API: sigue con el respaldo
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    document.body.removeChild(textarea);
    return Boolean(ok);
  } catch (err) {
    return false;
  }
};

/**
 * Web pública de empleos por organización (job board): /empleos/:slug
 * Lista las vacantes ATS activas (no confidenciales) de todos los proyectos
 * de la organización. "Postular" lleva al portal existente (/portal/apply/:id).
 *
 * Página de una vacante (/vacante/:positionId, 2026-10-25): es el link que
 * entrega el ATS. Muestra la web de empleos de la organización de la vacante
 * con esa vacante abierta en el detalle, aunque esté cerrada (marcada y sin
 * Postular). El correo se pide recién al apretar Postular. Si la organización
 * no tiene su web de empleos publicada, se muestra solo la vacante.
 */
const JobBoardLayout = ({ hostSlug = null }) => {
  const { slug: paramSlug, fairSlug: paramFairSlug, positionId: paramPositionId } = useParams();
  const navigate = useNavigate();
  // Página de una feria (/feria/:fairSlug, PLAN_FERIAS): solo sus vacantes,
  // sin links a la web general
  const fairSlug = paramFairSlug ? String(paramFairSlug).toLowerCase() : null;
  const isFair = Boolean(fairSlug);
  // Página de una vacante (/vacante/:positionId)
  const positionId = !isFair && paramPositionId ? String(paramPositionId) : null;
  const isPositionPage = Boolean(positionId);
  // El slug puede venir del subdominio ({slug}.amandahr.ai) o de la ruta (/empleos/:slug)
  const slug = isFair || isPositionPage ? null : (hostSlug || paramSlug);
  // Sin slug (/empleos) = portal GENERAL: agrega todas las orgs con web publicada
  const isGeneral = !slug && !isFair && !isPositionPage;
  // En la web de una organización y en la página de una vacante, elegir una
  // vacante cambia la dirección a la suya (/vacante/:id), para copiarla y
  // compartirla. En el portal general y en las ferias, no.
  const syncsUrl = isPositionPage || Boolean(slug);
  const { t, i18n } = useTranslation();
  // Formato de fecha chileno (DD-MM-YYYY) salvo que la app esté en inglés
  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : 'es-CL';
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [board, setBoard] = useState(null);
  const [positions, setPositions] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  // La página de una vacante abre directo el detalle en el celular
  const [showDetailMobile, setShowDetailMobile] = useState(isPositionPage);
  // Página de una vacante: la del link, que puede no estar en la lista de
  // abiertas (cerrada o pausada; null si no está disponible), con su estado
  // público ('open' | 'closed' | 'paused' | 'unavailable')
  const [linked, setLinked] = useState(null);
  // Sin web de empleos publicada no hay lista: solo la vacante
  const [boardPublished, setBoardPublished] = useState(true);
  // Id elegido en la lista cuya dirección se acaba de poner: ya está cargado
  const selectingRef = useRef(null);
  // "Copiar enlace": vacante cuyo enlace se acaba de copiar (el ícono pasa a
  // un check y el tooltip dice "Enlace copiado" por 2 segundos)
  const [copiedId, setCopiedId] = useState(null);
  const copiedTimerRef = useRef(null);
  // Tooltip del ícono: controlado, para mostrar "Enlace copiado" sin el mouse encima
  const [shareTipOpen, setShareTipOpen] = useState(false);
  // Misma clave de sesión que usa el portal de candidatos (PortalLayout)
  const [hasCandidateSession, setHasCandidateSession] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [fair, setFair] = useState(null);
  // Feria con sesión del candidato: correo para "Postulando como" y vacantes
  // de la feria a las que ya postuló
  const [sessionEmail, setSessionEmail] = useState(null);
  const [appliedIds, setAppliedIds] = useState([]);

  useEffect(() => {
    setHasCandidateSession(!!localStorage.getItem(SESSION_TOKEN_KEY));
  }, []);

  // Cerrar sesión desde el job board: mismo flujo que el portal (revoca en
  // backend, limpia el token local; si el backend falla igual cierra local).
  const handleLogout = async () => {
    setLoggingOut(true);
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    try {
      if (token) {
        await api.post(
          `/public/candidates/logout`,
          {},
          { headers: { 'x-candidate-token': token } }
        );
      }
    } catch (err) {
      console.error('Error closing session:', err);
    } finally {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      // Respuestas recordadas en ferias (PLAN_FERIAS, fase 2)
      clearFairAnswers();
      setHasCandidateSession(false);
      setSessionEmail(null);
      setAppliedIds([]);
      setLoggingOut(false);
    }
  };

  useEffect(() => {
    // La dirección cambió porque se eligió una vacante de la lista: ya está
    // cargada y en pantalla, no se vuelve a pedir
    if (isPositionPage && selectingRef.current === positionId) {
      selectingRef.current = null;
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setShowDetailMobile(isPositionPage);
        let url = `/job-boards${slug ? `/${slug}` : ''}`;
        if (isFair) url = `/job-fairs/${encodeURIComponent(fairSlug)}`;
        else if (isPositionPage) url = `/job-boards/position/${encodeURIComponent(positionId)}`;
        const res = await api.get(url);
        const data = res.data.response;
        if (data?.status_response === 'OK') {
          setBoard(data.board);
          setFair(data.fair || null);
          setPositions(data.positions || []);
          if (isPositionPage) {
            setLinked({ position: data.position || null, state: data.positionState || 'unavailable' });
            setBoardPublished(Boolean(data.boardPublished));
            setSelectedId(positionId);
          } else if (data.positions?.length > 0) {
            setSelectedId(data.positions[0].uid);
          }
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, fairSlug, isFair, isPositionPage, positionId]);

  // Feria con sesión: "Postulando como <correo>" (importante en una tablet
  // compartida del stand) y las vacantes a las que ya postuló
  useEffect(() => {
    if (!isFair) return undefined;
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) return undefined;
    let cancelled = false;
    const loadSession = async () => {
      try {
        const headers = { 'x-candidate-token': token };
        const me = await api.get(`/public/candidates/me`, { headers });
        if (cancelled || me.data.response?.status_response !== 'OK') return;
        setSessionEmail(me.data.response.data?.email || null);
        const apps = await api.get(`/applications`, { headers });
        if (!cancelled && apps.data.response?.status_response === 'OK') {
          setAppliedIds((apps.data.response.data || []).map((a) => a.positionId).filter(Boolean));
        }
      } catch (err) {
        console.error('Error loading candidate session:', err);
      }
    };
    loadSession();
    return () => { cancelled = true; };
  }, [isFair]);

  // Al salir de la página se cancela el timer de "Enlace copiado"
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  // Texto de la modalidad (se guarda como código: presencial / remoto / hibrido)
  const modalidadLabel = (value) =>
    (MODALIDADES.includes(value) ? t(`ats.positions.wizard.options.modalidad.${value}`) : value);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return positions;
    return positions.filter((p) =>
      [
        p.name, p.company?.name, p.ubicacion, p.area, p.industria, p.modalidad,
        MODALIDADES.includes(p.modalidad) ? t(`ats.positions.wizard.options.modalidad.${p.modalidad}`) : null,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term))
    );
  }, [positions, search, t]);

  // Página de una vacante: mientras la elegida sea la del link, el detalle
  // muestra esa vacante aunque no esté en la lista (cerrada o pausada) o el
  // aviso "no disponible"
  const linkedSelected = isPositionPage && linked && selectedId === positionId ? linked : null;
  const showUnavailable = Boolean(linkedSelected && linkedSelected.state === 'unavailable');
  const selected = showUnavailable
    ? null
    : filtered.find((p) => p.uid === selectedId)
      || (linkedSelected ? linkedSelected.position : null)
      || filtered[0]
      || null;
  // Estado público de la vacante del detalle: las de la lista reciben postulaciones
  const selectedState = linkedSelected && selected && selected.uid === positionId ? linkedSelected.state : 'open';

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    let date;
    const dateOnly = typeof timestamp === 'string' ? DATE_ONLY_RE.exec(timestamp) : null;
    if (dateOnly) date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    else if (timestamp._seconds) date = new Date(timestamp._seconds * 1000);
    else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
    else date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(dateLocale);
  };

  const handleSelect = (uid) => {
    setSelectedId(uid);
    if (!isDesktop) setShowDetailMobile(true);
    // La dirección pasa a la de la vacante elegida (ya está cargada: no se
    // vuelve a pedir). replace: no agrega una entrada al historial por cada clic
    if (syncsUrl && uid !== positionId) {
      setLinked({ position: positions.find((p) => p.uid === uid) || null, state: 'open' });
      setBoardPublished(true);
      selectingRef.current = uid;
      navigate(`/vacante/${encodeURIComponent(uid)}`, { replace: true });
    }
  };

  // Postular → portal de postulación. ?postular=1 lo distingue del link
  // antiguo /portal/apply/:id, que ahora lleva al detalle de la vacante
  const applyHref = (uid) => (isFair
    ? `/portal/apply/${uid}?feria=${encodeURIComponent(fairSlug)}`
    : `/portal/apply/${uid}?postular=1`);

  // "Copiar enlace" (2026-10-25): el link público de la vacante, el mismo que
  // entrega el ATS ({slug}.amandahr.ai/vacante/:id; desde el portal general,
  // con el subdominio de la empresa de esa vacante). En el celular abre el menú
  // de compartir del teléfono (WhatsApp, LinkedIn…); si no hay, copia. No va en
  // las ferias: su página no lleva a la web general de la organización.
  const canNativeShare = !isDesktop && typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const handleShare = async (position) => {
    const url = getPositionUrl(position.uid, position.boardSlug || board?.slug || null);
    if (canNativeShare) {
      try {
        await navigator.share({ title: position.name, url });
        return;
      } catch (err) {
        // Cerró el menú sin compartir: no se hace nada. Otro error: se copia
        if (err?.name === 'AbortError') return;
      }
    }
    if (await copyToClipboard(url)) {
      setCopiedId(position.uid);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedId(null), 2000);
    } else {
      // Último recurso: el enlace a la vista para copiarlo a mano
      window.prompt(t('jobBoard.share.copyManually'), url);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '15vh', px: 2 }}>
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4, textAlign: 'center', maxWidth: 480 }}>
          <WorkIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            {t(isFair
              ? 'jobBoard.fair.notFoundTitle'
              : isPositionPage ? 'jobBoard.vacancy.unavailableTitle' : 'jobBoard.notFoundTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: isFair ? 0 : 3 }}>
            {t(isFair
              ? 'jobBoard.fair.notFoundBody'
              : isPositionPage ? 'jobBoard.vacancy.unavailableBody' : 'jobBoard.notFoundBody')}
          </Typography>
          {/* La página de una feria no lleva a la web general (PLAN_FERIAS) */}
          {!isFair && (
            <Button
              variant="contained"
              // El path funciona también desde subdominios inexistentes:
              // /empleos siempre renderiza el portal general
              href="/empleos"
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.2, px: 4 }}
            >
              {t('jobBoard.viewAllJobs')}
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  // Página de una vacante de una organización sin web de empleos publicada:
  // solo la vacante, sin título, buscador ni lista
  const showList = !(isPositionPage && !boardPublished);
  const listVisible = showList && (isDesktop || !showDetailMobile);
  const detailVisible = !showList || isDesktop || showDetailMobile;
  // En el celular, volver a la lista desde el detalle (en la página de una
  // vacante se llega directo al detalle: "Ver vacantes abiertas")
  const backToListButton = showList && !isDesktop ? (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => setShowDetailMobile(false)}
      sx={{ mb: 1, color: 'text.secondary', textTransform: 'none' }}
    >
      {t(isPositionPage ? 'jobBoard.vacancy.seeOpenPositions' : 'jobBoard.backToList')}
    </Button>
  ) : null;

  const renderSection = (titleKey, content) =>
    content ? (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {t(titleKey)}
        </Typography>
        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
          {content}
        </Typography>
      </Box>
    ) : null;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <PortalBrandingHeader branding={board} />

      {/* Topbar de cuenta del candidato: "Ingresar" sin sesión; con sesión,
          navegación a Mis postulaciones / Mi perfil (vía /portal) y logout */}
      <PortalHeader
        hasSession={hasCandidateSession}
        useLinks
        current="portal"
        onLogout={handleLogout}
        loggingOut={loggingOut}
        fairSlug={fairSlug}
        sessionEmail={sessionEmail}
      />

      <Box sx={{ maxWidth: showList ? 1200 : 900, mx: 'auto', px: 2, py: 3 }}>
        {/* Título + buscador */}
        {showList && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            {isGeneral && (
              <Box
                component="img"
                src="/amandalogoh.png"
                alt="Amanda HR"
                sx={{ height: 40, mb: 2, display: 'block', mx: 'auto' }}
              />
            )}
            <Typography variant="h5" fontWeight={700}>
              {isFair
                ? fair?.name
                : isGeneral
                  ? t('jobBoard.generalTitle')
                  : board?.companyName
                    ? t('jobBoard.titleWithCompany', { company: board.companyName })
                    : t('jobBoard.title')}
            </Typography>
            {isFair && board?.companyName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('jobBoard.fair.subtitle', { company: board.companyName })}
              </Typography>
            )}
            <TextField
              placeholder={t('jobBoard.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ mt: 2, width: '100%', maxWidth: 480, backgroundColor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Lista de vacantes */}
          {listVisible && (
            <Box sx={{ width: isDesktop ? 380 : '100%', flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('jobBoard.jobsCount', { count: filtered.length })}
              </Typography>

              {filtered.length === 0 && (
                <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {isFair && positions.length === 0 ? t('jobBoard.fair.empty') : t('jobBoard.empty')}
                  </Typography>
                </Paper>
              )}

              {filtered.map((p) => (
                <Paper
                  key={p.uid}
                  elevation={0}
                  onClick={() => handleSelect(p.uid)}
                  sx={{
                    border: '1px solid',
                    borderColor: selected?.uid === p.uid && isDesktop ? '#6A5AF9' : '#E5E7EB',
                    borderRadius: 2,
                    p: 2,
                    mb: 1.5,
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#6A5AF9' },
                    display: 'flex',
                    gap: 1.5,
                  }}
                >
                  {p.company?.logoUrl && (
                    <Avatar src={p.company.logoUrl} variant="rounded" sx={{ width: 44, height: 44 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#2563EB' }}>
                      {p.name}
                    </Typography>
                    {p.company?.name && (
                      <Typography variant="body2" fontWeight={600}>
                        {p.company.name}
                      </Typography>
                    )}
                    {(p.ubicacion || p.modalidad) && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {[p.ubicacion, modalidadLabel(p.modalidad)].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    {formatDate(p.created) && (
                      <Typography variant="caption" color="text.secondary">
                        {t('jobBoard.publishedOn')} {formatDate(p.created)}
                      </Typography>
                    )}
                    {appliedIds.includes(p.uid) && (
                      <Chip
                        label={t('jobBoard.fair.alreadyApplied')}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }}
                      />
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {/* Detalle: la vacante del link no está disponible (borrador,
              eliminada u oculta del portal): no se muestra nada del cargo */}
          {detailVisible && showUnavailable && (
            <Paper
              elevation={0}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, flexGrow: 1, minWidth: 0 }}
            >
              {backToListButton}
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <WorkIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  {t('jobBoard.vacancy.unavailableTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('jobBoard.vacancy.unavailableBody')}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Detalle */}
          {detailVisible && selected && (
            <Paper
              elevation={0}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, flexGrow: 1, minWidth: 0 }}
            >
              {backToListButton}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
                  {selected.company?.logoUrl && (
                    <Avatar src={selected.company.logoUrl} variant="rounded" sx={{ width: 52, height: 52 }} />
                  )}
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#2563EB' }}>
                      {selected.name}
                    </Typography>
                    {selected.company?.name && (
                      <Typography variant="body2" color="text.secondary">
                        {t('jobBoard.publishedBy')} <b>{selected.company.name}</b>
                      </Typography>
                    )}
                    {isGeneral && selected.boardSlug && (
                      <Link
                        // En producción la web de cada org vive en su subdominio
                        // {slug}.amandahr.ai; en localhost cae a /empleos/:slug
                        href={getBoardUrl(selected.boardSlug)}
                        variant="body2"
                        underline="hover"
                        sx={{ color: '#6A5AF9' }}
                      >
                        {t('jobBoard.companyJobs', { company: selected.company?.name || '' })}
                      </Link>
                    )}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                    {selectedState !== 'open' ? (
                      // Cerrada o pausada: se ve marcada y sin Postular
                      <Chip
                        label={t(selectedState === 'closed' ? 'jobBoard.vacancy.closed' : 'jobBoard.vacancy.paused')}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : appliedIds.includes(selected.uid) ? (
                      <Button variant="outlined" disabled sx={{ px: 4 }}>
                        {t('jobBoard.fair.alreadyApplied')}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        // Desde una feria la postulación queda marcada con ella (PLAN_FERIAS)
                        href={applyHref(selected.uid)}
                        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, px: 4 }}
                      >
                        {t('jobBoard.apply')}
                      </Button>
                    )}
                    {!isFair && (
                      // Copiar enlace (en el celular, compartir); no en las ferias.
                      // Solo el ícono; el texto va en el tooltip (y es su aria-label)
                      <Tooltip
                        title={copiedId === selected.uid
                          ? t('jobBoard.share.copied')
                          : t(canNativeShare ? 'jobBoard.share.share' : 'jobBoard.share.copy')}
                        open={shareTipOpen || copiedId === selected.uid}
                        onOpen={() => setShareTipOpen(true)}
                        onClose={() => setShareTipOpen(false)}
                      >
                        <IconButton
                          onClick={() => handleShare(selected)}
                          sx={{ color: copiedId === selected.uid ? 'success.main' : '#6A5AF9' }}
                        >
                          {copiedId === selected.uid ? <CheckIcon /> : canNativeShare ? <ShareIcon /> : <LinkIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {formatDate(selected.fechaCierre) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('portal.closesOn')} {formatDate(selected.fechaCierre)}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
                {[modalidadLabel(selected.modalidad), selected.tipoEmpleo, selected.ubicacion, selected.area || selected.industria]
                  .filter(Boolean)
                  .map((tag) => (
                    <Chip key={tag} label={tag} size="small" icon={tag === selected.ubicacion ? <PlaceIcon /> : undefined} />
                  ))}
              </Box>

              {selectedState !== 'open' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t(selectedState === 'closed' ? 'jobBoard.vacancy.closedBody' : 'jobBoard.vacancy.pausedBody')}
                </Alert>
              )}

              <Divider sx={{ mb: 2.5 }} />

              {renderSection('portal.positionDescription', selected.descripcion)}
              {renderSection('portal.positionResponsabilities', selected.responsabilidades)}
              {renderSection('portal.positionProfile', selected.perfil)}
              {renderSection('portal.positionRequiredKnowledge', selected.conocimientosExcluyentes)}
              {renderSection('portal.positionDesirableKnowledge', selected.conocimientosDeseables)}
              {/* Beneficios del cargo [{ nombre, descripcion }] */}
              {Array.isArray(selected.beneficios) && selected.beneficios.some((b) => b?.nombre) && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    {t('portal.positionBenefits')}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {selected.beneficios.filter((b) => b?.nombre).map((b, i) => (
                      <Typography component="li" variant="body2" color="text.primary" key={i}>
                        <strong>{b.nombre}</strong>{b.descripcion ? ` — ${b.descripcion}` : ''}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default JobBoardLayout;
